import React from "react";
import UserAvatar from "../../components/UserAvatar";
import {
  FaBuilding,
  FaMapMarkerAlt,
  FaUser,
  FaIndustry,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaChartLine,
  FaInfoCircle,
  FaEnvelope,
  FaPhone,
} from "react-icons/fa";

function TenantListItem({ tenant }) {
  // Calculate days elapsed since creation
  const daysElapsed = tenant.createdAt
    ? Math.floor(
        (new Date() - tenant.createdAt.toDate()) / (1000 * 60 * 60 * 24)
      )
    : 0;

  // Determine status style
  const getStatusStyle = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return "bg-green-100 text-green-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "on hold":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="bg-white border border-gray-100 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden cursor-pointer">
      <div className="flex flex-col md:flex-row">
        {/* Left Section with Key Info */}
        <div className="md:w-64 bg-gradient-to-r from-[#002368] to-[#002368]/90 text-white p-4 flex flex-col justify-between">
          <div>
            <h3 className="font-semibold text-lg mb-1 line-clamp-2">
              {tenant.tenantName}
            </h3>
            <div className="flex items-center text-xs text-white/80 mb-2">
              <FaIndustry className="mr-1" />{" "}
              {tenant.tenantIndustry || "Industry not specified"}
            </div>

            {/* Status Badge */}
            <div
              className={`inline-block text-xs px-2 py-1 rounded-full font-medium mt-1 ${getStatusStyle(
                tenant.status
              )}`}
            >
              {tenant.status || "No Status"}
            </div>
          </div>

          {/* Pipeline Progress */}
          {tenant.pipeline_status && (
            <div className="mt-auto">
              <div className="flex items-center justify-between text-xs mb-1">
                <span>Pipeline Progress</span>
                <span>{tenant.pipeline_status}</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-1.5">
                <div
                  className="bg-[#ff5003] h-1.5 rounded-full"
                  style={{ width: tenant.pipeline_status || "0%" }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4">
          {/* Top Row - Contact Info & Creation Date */}
          <div className="flex flex-col md:flex-row justify-between mb-4">
            <div className="flex flex-col gap-1">
              {tenant.tenantContactPerson && (
                <div className="flex items-center text-sm text-gray-700">
                  <FaUser className="text-[#ff5003] mr-2" />
                  {tenant.tenantContactPerson}
                </div>
              )}
              {tenant.tenantEmail && (
                <div className="flex items-center text-sm text-gray-700">
                  <FaEnvelope className="text-[#ff5003] mr-2" />
                  {tenant.tenantEmail}
                </div>
              )}
              {tenant.tenantContactNumber && (
                <div className="flex items-center text-sm text-gray-700">
                  <FaPhone className="text-[#ff5003] mr-2" />
                  {tenant.tenantContactNumber}
                </div>
              )}
            </div>

            <div className="text-xs text-gray-500 mt-2 md:mt-0 flex items-center">
              <span className="bg-gray-100 px-2 py-1 rounded">
                {daysElapsed === 0 ? "Added today" : `${daysElapsed} days ago`}
              </span>
            </div>
          </div>

          {/* Middle Row - Requirement Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 border-t border-b border-gray-100 py-3">
            <div className="flex items-start">
              <FaBuilding className="text-[#ff5003] mt-1 mr-2 flex-shrink-0" />
              <div>
                <div className="text-xs text-gray-500">Size</div>
                <div className="font-medium">{tenant.requirementSize} sqm</div>
              </div>
            </div>

            <div className="flex items-start">
              <FaMapMarkerAlt className="text-[#ff5003] mt-1 mr-2 flex-shrink-0" />
              <div>
                <div className="text-xs text-gray-500">Location</div>
                <div className="font-medium">
                  {tenant.requirementLocation || "Not specified"}
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <FaMoneyBillWave className="text-[#ff5003] mt-1 mr-2 flex-shrink-0" />
              <div>
                <div className="text-xs text-gray-500">Budget</div>
                <div className="font-medium">
                  {tenant.requirementBudget
                    ? new Intl.NumberFormat("en-US", {
                        style: "currency",
                        currency: "PHP",
                        maximumFractionDigits: 0,
                      }).format(tenant.requirementBudget)
                    : "Not specified"}
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <FaCalendarAlt className="text-[#ff5003] mt-1 mr-2 flex-shrink-0" />
              <div>
                <div className="text-xs text-gray-500">Move-in</div>
                <div className="font-medium">
                  {tenant.requirementMoveInDate
                    ? new Date(
                        tenant.requirementMoveInDate
                      ).toLocaleDateString()
                    : "Not specified"}
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Row - Team & Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="text-xs text-gray-500 mr-2">Team:</div>
              <div className="flex items-center flex-wrap">
                {tenant.team_members?.map(
                  (member, index) =>
                    index < 4 && (
                      <div key={index} className="inline-block ml-1 first:ml-0">
                        <UserAvatar email={member} size="sm" />
                      </div>
                    )
                )}
                {tenant.team_members?.length > 4 && (
                  <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center text-xs text-gray-700 border border-white ml-1">
                    +{tenant.team_members.length - 4}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button className="flex items-center gap-1 bg-white border border-[#002368] text-[#002368] px-3 py-1.5 rounded-lg text-sm font-medium hover:bg-[#002368] hover:text-white transition-colors duration-200">
                <FaInfoCircle size={14} /> View Details
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TenantListItem;
